import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, Stack, IconButton, Box, Typography, Divider, Slide, Grid, Chip, Tooltip, Button, useMediaQuery } from '../../../ui/core';
import { useTheme } from '../../../ui/styles';
import { ContentCopyIcon, CloseFullscreenIcon, OpenFullscreenIcon, PhoneIcon, StationIcon, CloseIcon, InfoIcon, WarningIcon } from '../../../ui/icons';
import moment from 'moment';
import { get, post } from '../../../helpers/api';

import { useFormatters, useCopyToClipboard, useToast, useActiveExclusionMatchSet } from '../../../hooks';
import { getTripETA } from '../../../redux/Trips/actions';
import { useDispatch } from 'react-redux';
import { RideStatus } from '../../../models/Rides';
import { isGhostTrip } from '../../../helpers/utils/trip';
import { getExcludedRiderIds } from '../../../helpers/utils/riderDriverExclusions';

import TripStatusChip from './TripStatusChip';
import RidersList from './RidersList';
import TripMap from './TripMap';
import TripLocationHistory from './TripLocationHistory';
import DriverLocation from './DriverLocation';
import TripDriverField from './TripDriverField';
import TripTagsField from './TripTagsField';
import TripPayoutField from './TripPayoutField';
import UserCard from './UserCard';
import TripOverrideCitiesField from './TripOverrideCitiesField';
import TripItinerary from './TripItinerary';
import TripActivity from './TripActivity';
import TripEventLog from './TripEventLog';
import ErrorBoundary from '../../shared/ErrorBoundary';
import ErrorIcon from '../../../ui/icons/ErrorIcon';
import AdjustTripTimeDialog from './AdjustTripTimeDialog';
import { useHasPermission } from '../../../helpers/api/auth/index';
import { PERMISSIONS } from '../../../helpers/api/auth/permissions';
import { getDispatchDelayBaseline, getOpsAdjustmentBaseline } from '../../../models/Trips';

const style = {
  heading: {
    padding: '12px 16px',
  },
  section: {
    padding: '32px 48px',
  },
  // Two columns (label | value) on desktop. On a phone a 124px label column leaves
  // under 200px for values like a driver row or a payout with its chips, so the list
  // stacks into one column with the label above its value. The fixed 48px row height
  // goes with it — stacked rows have to grow to their content instead of clipping.
  dataList: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: '124px 1fr' },
    rowGap: { xs: 0, md: '8px' },
    margin: 0,
    // Mobile keeps the stack but tightens it hard. Desktop's fixed 48px rows and
    // 16px body type turned seven fields into two screens of scrolling before the
    // riders were even visible; the label drops to a small secondary caption and the
    // value to the same 15px the trip cards use, so the dialog reads as a denser
    // continuation of the list it opened from rather than a different, larger app.
    '> dt': {
      gridColumn: 1,
      height: { md: '48px' },
      display: 'flex',
      alignItems: 'center',
      '.MuiTypography-root': { fontSize: { xs: '12.5px', md: 'inherit' }, lineHeight: { xs: 1.4, md: 'inherit' } },
    },
    '> dd': {
      gridColumn: { xs: 1, md: 2 },
      height: { md: '48px' },
      display: 'flex',
      alignItems: 'center',
      // Values that pair a figure with chips (payout, confirmation) overflow a
      // narrow column otherwise.
      flexWrap: { xs: 'wrap', md: 'nowrap' },
      mt: { xs: '1px', md: 0 },
      mb: { xs: '14px', md: 0 },
      '.MuiTypography-root': { fontSize: { xs: '15px', md: 'inherit' } },
    },
  },
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" timeout={900} ref={ref} {...props} />;
});

// Gets a URL for a tripId that maintains the existing properties of the current trip/search
const getTripURL = (tripId) => {
  let urlObj = new URL(window.location.href);
  let params = new URLSearchParams(urlObj.search);

  params.set('tripId', tripId);
  urlObj.search = params.toString();

  return urlObj.toString();
};

// Helper: calculate distance between two coordinates in miles
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  return distance;
};

// Helper: get driver arrival on-time status for pickup actions
const getDriverArrivalStatus = (action, ride, locationData) => {
  if (!locationData.length || !action.timestamp || !ride.pickUp?.coords?.coordinates) return null;

  const pickupMs = Date.parse(action.timestamp);
  const pickupCoords = ride.pickUp.coords.coordinates;

  // Find all points within 500 meters (approximately 0.31 miles) of pickup coordinates and before pickup time
  const nearbyPoints = locationData.filter(point => {
    const pointMs = Date.parse(point.timestamp);
    const timeDiff = pickupMs - pointMs; // Only points before pickup

    if (timeDiff < 0) return false; // Must be before pickup

    const distance = calculateDistance(
      point.coordinate.lat,
      point.coordinate.lon,
      pickupCoords[1], // lat
      pickupCoords[0]  // lon
    );

    return distance <= 0.31; // 500 meters ≈ 0.31 miles
  });

  if (nearbyPoints.length === 0) {
    return {
      status: 'unknown',
      label: 'Driver arrival unknown',
      color: 'text.secondary'
    };
  }

  // Find the earliest point within acceptable radius
  const earliestPoint = nearbyPoints.sort((a, b) =>
    Date.parse(a.timestamp) - Date.parse(b.timestamp)
  )[0];

  // Determine scheduled departure time
  const history = ride.departureTimeHistory || [];
  const scheduledDepartureIso = (history[0] && history[0].pastDeptTime)
    ? history[0].pastDeptTime
    : ride.departureTime;

  if (!scheduledDepartureIso) return null;

  const scheduledMs = Date.parse(scheduledDepartureIso);
  const arrivalMs = Date.parse(earliestPoint.timestamp);

  if (Number.isNaN(scheduledMs) || Number.isNaN(arrivalMs)) return null;

  const minutesLate = Math.max(0, Math.round((arrivalMs - scheduledMs) / 60000));
  const label = minutesLate === 0 ? 'Driver on time' : `Driver ${minutesLate} minutes late`;
  const color = minutesLate === 0 ? 'success.main' : minutesLate <= 15 ? 'warning.main' : 'error.main';

  return {
    status: minutesLate === 0 ? 'on_time' : 'late',
    minutesLate,
    label,
    color,
    arrivalTime: earliestPoint.timestamp
  };
};

const CONFIRMATION_STATUS_DESCRIPTIONS = {
  PRE_CONFIRMATION:
    "The driver booked far in advance. They'll receive confirmation reminders as the trip approaches but can't yet confirm in the app.",
  UNCONFIRMED:
    "The confirmation window is open. The driver has been asked to confirm but hasn't responded yet.",
  CONFIRMED: 'The driver has confirmed and the trip is locked in with them.',
  PENDING_REASSIGNMENT:
    "The driver didn't confirm by the deadline. They should be cancelled off this trip so it can be reassigned.",
};

// Helper: get confirmation status chip styling
const getConfirmationStatusChip = (status) => {
  if (!status) return null;

  const statusConfig = {
    PRE_CONFIRMATION: { label: 'Pre-Confirmation', color: 'default' },
    CONFIRMED: { label: 'Confirmed', color: 'success' },
    UNCONFIRMED: { label: 'Unconfirmed', color: 'default' },
    PENDING_REASSIGNMENT: { label: 'Pending Reassignment', color: 'error' },
  };

  const config = statusConfig[status] || { label: status, color: 'default' };

  return <Chip label={config.label} color={config.color} size="small" />;
};

// Helper: get payment status chip styling
const getPaymentStatusChip = (status) => {
  if (!status) return null;

  const statusConfig = {
    pending: { label: 'Pending', color: 'warning' },
    completed: { label: 'Completed', color: 'info' },
    failed: { label: 'Failed', color: 'error' },
  };

  const config = statusConfig[status.toLowerCase()] || { label: status, color: 'default' };

  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      sx={{ ml: 1 }}
    />
  );
};

// Helper: get drop-off location info
const getDropoffLocationInfo = (action, ride, locationData) => {
  if (!locationData.length || !action.timestamp) return null;

  const targetMs = Date.parse(action.timestamp);
  const twoMinutesMs = 2 * 60 * 1000; // 2 minutes in milliseconds

  // Find all points within 2 minutes
  const nearbyPoints = locationData.filter(point => {
    const pointMs = Date.parse(point.timestamp);
    const timeDiff = Math.abs(pointMs - targetMs);
    return timeDiff <= twoMinutesMs;
  });

  if (nearbyPoints.length === 0) return null;

  // Find the closest point by time
  const locationPoint = nearbyPoints.reduce((closest, current) => {
    const closestDiff = Math.abs(Date.parse(closest.timestamp) - targetMs);
    const currentDiff = Math.abs(Date.parse(current.timestamp) - targetMs);
    return currentDiff < closestDiff ? current : closest;
  });

  // Calculate distance if we have scheduled coordinates
  if (ride.dropOff?.coords?.coordinates) {
    const scheduledCoords = ride.dropOff.coords.coordinates;
    const distance = calculateDistance(
      locationPoint.coordinate.lat,
      locationPoint.coordinate.lon,
      scheduledCoords[1], // lat
      scheduledCoords[0]  // lon
    );

    const locationTime = new Date(locationPoint.timestamp).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    const color = distance <= 0.2 ? 'success.main' : distance < 1 ? 'warning.main' : 'error.main';

    return {
      distance: distance.toFixed(1),
      locationTime,
      color,
      actualLocation: locationPoint.coordinate,
      scheduledLocation: { lat: scheduledCoords[1], lng: scheduledCoords[0] }
    };
  }

  return null;
};

// Helper: compute on-time status for pickup or drop-off actions
const getOnTimeStatus = (ride, action) => {
  const isPickup = action.status === RideStatus.en_route;

  // HITCH-4018: Don't show status for drop-off delays finding driver, since they will be
  // incorrect. Ops adjustments don't have this problem — they shift departureTime and
  // arrivalTime by the same delta, so the scheduled drop-off stays coherent — so only
  // non-ops history entries (including legacy entries with no `reason`) trigger this.
  if (!isPickup && ride.departureTimeHistory?.some((h) => h.reason !== 'ops_adjustment')) {
    return {
      status: 'unknown',
      label: 'Delay unknown',
      color: 'text.secondary'
    };
  }

  let scheduledTimeIso;
  if (isPickup) {
    const history = ride.departureTimeHistory || [];
    scheduledTimeIso = (history[0] && history[0].pastDeptTime)
      ? history[0].pastDeptTime
      : ride.departureTime;
  } else {
    scheduledTimeIso = ride.arrivalTime;
  }

  if (!scheduledTimeIso || !action.timestamp) return null;

  const scheduledMs = Date.parse(scheduledTimeIso);
  const actualMs = Date.parse(action.timestamp);

  if (Number.isNaN(scheduledMs) || Number.isNaN(actualMs)) return null;

  const minutesLate = Math.max(0, Math.round((actualMs - scheduledMs) / 60000));
  const label = minutesLate === 0 ? 'On time' : `${minutesLate} minutes late`;
  const color = minutesLate === 0 ? 'success.main' : minutesLate <= 15 ? 'warning.main' : 'error.main';

  return {
    status: minutesLate === 0 ? 'on_time' : 'late',
    minutesLate,
    label,
    color,
    scheduledTime: scheduledTimeIso,
    actualTime: action.timestamp
  };
};

// Helper: enrich trip data with calculated pickup/dropoff information
const enrichTripData = (fullTripData, locationData) => {
  if (!fullTripData) return fullTripData;

  // Helper function to enrich a single ride
  const enrichRide = (ride) => {
    if (!ride) return ride;

    const enrichedRide = { ...ride };

    // Pricing enrichment
    const fare = ride.fare || {};
    const totalCreditsUsed = (fare.creditUsed || 0)
      + (fare.cancellationCreditUsed || 0)
      + (fare.cancellationReferralCreditUsed || 0)
      + (fare.referralCreditUsed || 0);
    const totalPrice = (fare.finalAmount || 0) + totalCreditsUsed;
    enrichedRide.totalCreditsUsed = totalCreditsUsed;
    enrichedRide.totalPrice = totalPrice;

    // Find pickup and dropoff actions
    const pickupAction = ride.statusUpdates?.find(u => u.status === RideStatus.en_route);
    const dropoffAction = ride.statusUpdates?.find(u => u.status === RideStatus.completed);

    // Calculate pickup information
    if (pickupAction) {
      enrichedRide.pickupInfo = {
        action: pickupAction,
        onTimeStatus: getOnTimeStatus(ride, pickupAction),
        driverArrivalStatus: getDriverArrivalStatus(pickupAction, ride, locationData)
      };
    }

    // Calculate dropoff information
    if (dropoffAction) {
      enrichedRide.dropoffInfo = {
        action: dropoffAction,
        onTimeStatus: getOnTimeStatus(ride, dropoffAction),
        locationInfo: getDropoffLocationInfo(dropoffAction, ride, locationData)
      };
    }

    return enrichedRide;
  };

  // Enrich rides
  if (fullTripData.rides) {
    fullTripData.rides = fullTripData.rides.map(enrichRide);
  }

  // Enrich cancelledRides
  if (fullTripData.cancelledRides) {
    fullTripData.cancelledRides = fullTripData.cancelledRides.map(enrichRide);
  }

  return fullTripData;
};

const TripDetailsDialog = ({ trip, fullScreen, outboundTrip, returnTrip, onClose, onFullscreen }) => {
  const theme = useTheme();
  // Below `md` the dialog is the whole screen, so the two-pane fullScreen layout and
  // the toggle that produces it have nothing to switch between.
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const f = useFormatters();
  const c = useCopyToClipboard();
  const t = useToast();
  const dispatch = useDispatch();
  const [locationData, setLocationData] = useState([]);
  const [fullTripData, setFullTripData] = useState(null);
  const canAdjustTripTime = useHasPermission(PERMISSIONS.ADJUST_TRIP_TIME);
  const [adjustTimeOpen, setAdjustTimeOpen] = useState(false);

  const vehicle = trip && trip.driver ? trip.driver.vehicles.find((v) => v.active) : null;

  // HITCH-5300: only against active `rides`, never `cancelledRides` — a
  // cancelled-off rider never actually rode with this driver.
  const { matchSet: exclusionMatchSet, isError: exclusionMatchError } = useActiveExclusionMatchSet();
  const excludedRiderIds = getExcludedRiderIds(
    trip?.driver?.id,
    (trip?.rides || []).map((ride) => ride.rider),
    exclusionMatchSet,
  );

  const copyIdToClipboard = () => {
    c.copy(trip.id);
    t.success('Trip id copied to clipboard');
  };

  const fetchFullTripData = async (isCancelled = () => false) => {
    if (!trip?.id) return;

    try {
      const response = await post(`/admin/trip/full/${trip.id}`);
      if (isCancelled()) return;
      const enrichedData = enrichTripData(response.data, locationData || []);
      setFullTripData(enrichedData);
    } catch (err) {
      console.error('Error fetching full trip data:', err);
      if (!isCancelled()) setFullTripData(null);
    }
  };

  // Fetch full trip data for make it right form
  useEffect(() => {
    let cancelled = false;
    fetchFullTripData(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [trip?.id, locationData]);

  // Fetch location history for the trip
  useEffect(() => {
    let cancelled = false;
    const fetchLocationHistory = async () => {
      if (!trip?.id || !trip?.driver) return;

      try {
        const response = await get(`/admin/location/trip/${trip.id}`);
        if (cancelled) return;
        const allData = response.data || [];
        setLocationData(allData);
      } catch (err) {
        console.error('Error fetching location history:', err);
        if (!cancelled) setLocationData([]);
      }
    };

    fetchLocationHistory();
    return () => {
      cancelled = true;
    };
  }, [trip?.id, trip?.driver]);

  const getPrevNextTripComponent = (trip, _fullScreen) => {
    return (
      <>
        <Box>
          <Stack direction="row" spacing="24px" mb="20px">
            <Typography sx={{ fontSize: '20px' }}>
              {trip.nextTrip && `Outbound`}
              {trip.previousTrip && `Return`}
            </Typography>
            <Box display="flex" alignItems="center">
              <StationIcon sx={{ fontSize: '16px', color: 'text.secondary' }} />
              <Typography color="text.secondary">
                <a href={getTripURL(trip.id)}>{trip.id}</a>
              </Typography>
            </Box>
          </Stack>
          <Box component="dl" direction="row" sx={style.dataList}>
            <dt>
              <Typography color="text.secondary">Departure</Typography>
            </dt>
            <dd>
              <Typography>
                {f.date.toTimezoneLocaleString(
                  trip.departureTime,
                  trip.originCity.timeZone,
                  f.date.DateFormats.TIME_SIMPLE_WITH_TIMEZONE,
                )}
              </Typography>
            </dd>
            <dt>
              <Typography color="text.secondary">Riders</Typography>
            </dt>
            <Stack component="dd" direction="row" spacing={3}>
              {trip.rides.map((ride) => {
                const rideId = ride.id || ride.rideId;
                return <UserCard key={rideId} user={ride.rider} />;
              })}
            </Stack>
            <dt>
              <Typography color="text.secondary">Payout</Typography>
            </dt>
            <dd>{f.number.currency(trip.payout)}</dd>
          </Box>
        </Box>
      </>
    );
  };

  useEffect(() => {
    if (trip?.id) {
      getTripETA(trip.id)(dispatch);
    }
  }, [trip?.id, dispatch]);

  // Prefer the freshly-refetched trip/rides over the (possibly stale) `trip`
  // prop, so a successful adjustment is reflected immediately instead of
  // waiting on the parent list's own poll cycle.
  //
  // IMPORTANT: `fullTripData.trip` comes from `POST /admin/trip/full/:tripId`,
  // which returns the RAW Mongo Trip document -- not the GraphQL-resolved
  // shape `trip` carries. Reference fields that GraphQL resolves to full
  // objects (originCity, destinationCity, driver, rides[].rider, ...) are
  // bare refs on the raw document (e.g. `originCity: { city_id }`, no
  // `timeZone`/`cityName`). A blanket `{ ...trip, ...fullTripData.trip }`
  // spread clobbers every one of those resolved fields with its unresolved
  // raw counterpart -- do NOT reintroduce that. Instead, only overlay the
  // specific fields that need to be fresh after an adjustment, keeping the
  // GraphQL shape for everything else.
  const rideKey = (ride) => String(ride?.id ?? ride?.rideId ?? ride?._id);

  const mergeRide = (graphqlRide, rawRide) => {
    if (!rawRide) return graphqlRide;
    if (!graphqlRide) return rawRide;
    return {
      ...graphqlRide,
      departureTime: rawRide.departureTime,
      departureTimeHistory: rawRide.departureTimeHistory,
    };
  };

  const mergeRides = (graphqlRides, rawRides) => {
    if (!rawRides) return graphqlRides;
    const rawByKey = new Map(rawRides.map((r) => [rideKey(r), r]));
    const matchedKeys = new Set();
    const merged = (graphqlRides || []).map((gr) => {
      const key = rideKey(gr);
      matchedKeys.add(key);
      return mergeRide(gr, rawByKey.get(key));
    });
    // A raw ride with no GraphQL counterpart (e.g. added after the initial
    // GraphQL fetch) must still show up rather than being silently dropped.
    rawRides.forEach((rr) => {
      if (!matchedKeys.has(rideKey(rr))) merged.push(rr);
    });
    return merged;
  };

  const displayTrip = trip && fullTripData?.trip
    ? {
      ...trip,
      departureTime: fullTripData.trip.departureTime,
      rides: mergeRides(trip.rides, fullTripData.rides || fullTripData.trip.rides),
    }
    : trip;

  const dispatchDelayBaseline = displayTrip ? getDispatchDelayBaseline(displayTrip) : null;
  const opsAdjustmentBaseline = displayTrip ? getOpsAdjustmentBaseline(displayTrip) : null;
  const dispatchDelayMinutes = dispatchDelayBaseline
    ? moment(displayTrip.departureTime).diff(moment(dispatchDelayBaseline), 'minutes')
    : 0;

  return (
    <Dialog
      sx={{
        left: 'auto',
        width: { xs: '100%', md: fullScreen ? '100%' : '60%' },
        // The 840px floor is what keeps the two-pane desktop layout readable; on a
        // 375px screen it just pushed the dialog off the side of the viewport.
        minWidth: { xs: 0, md: '840px' },
      }}
      fullScreen
      open={!!trip}
      TransitionComponent={Transition}
      onClose={onClose}
    >
      <Stack
        position="relative"
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        spacing={{ xs: '10px', md: '16px' }}
        py="6px"
        // Desktop reserves a left gutter for the absolutely-positioned close button
        // and a right one for the fullscreen toggle. On a phone this bar wraps onto
        // two or three lines, and an absolutely-centred close button then floats over
        // the second line's content — so there it becomes an ordinary first child
        // instead, and the gutters go away.
        pl={{ xs: '4px', md: '48px' }}
        pr={{ xs: '12px', md: '48px' }}
      >
        <IconButton
          sx={{
            position: { xs: 'static', md: 'absolute' },
            top: { md: '50%' },
            transform: { xs: 'none', md: 'translateY(-50%)' },
            left: { md: '8px' },
          }}
          onClick={onClose}
        >
          <CloseIcon sx={{ color: 'text.secondary' }} />
        </IconButton>
        {trip && (
          <>
            <Box display="flex" alignItems="center">
              <StationIcon sx={{ fontSize: '16px' }} />
              <Typography ml="5px" mr="10px">
                Trip {trip.id}
              </Typography>
              <IconButton size="small" onClick={copyIdToClipboard}>
                <ContentCopyIcon sx={{ fontSize: '16px' }} />
              </IconButton>
            </Box>
            <TripStatusChip status={trip.status} />
            <TripTagsField trip={trip} />
          </>
        )}
        {!compact && (
          <IconButton
            sx={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: '8px' }}
            onClick={() => onFullscreen(!fullScreen)}
          >
            {fullScreen ? (
              <CloseFullscreenIcon sx={{ fontSize: '24px' }} />
            ) : (
              <OpenFullscreenIcon sx={{ fontSize: '24px' }} />
            )}
          </IconButton>
        )}
      </Stack>
      <Divider />
      <DialogContent sx={{ padding: { xs: '20px 16px', md: '32px 40px' } }}>
        <Grid container spacing={{ xs: '16px', md: '32px' }}>
          {/* Always one column below `md`: side-by-side details and maps on a phone
              gives each of them half of 375px. */}
          <Grid item xs={12} md={fullScreen ? true : 12}>
            <Stack spacing="24px">
              {trip && (
                <Box display="flex" alignItems="center" flexWrap="wrap" rowGap="8px">
                  {isGhostTrip(trip) && (
                    <Typography sx={{ fontSize: { xs: '22px', md: '28px' }, lineHeight: '32px', mr: '12px' }}>👻</Typography>
                  )}
                  <Typography sx={{ fontSize: { xs: '20px', md: '28px' }, lineHeight: '32px', fontWeight: { xs: 600, md: 400 } }}>
                    {trip.originCity.cityName} ➔ {trip.destinationCity.cityName}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing="4px" ml="18px" mr="8px">
                    <StationIcon sx={{ fontSize: '16px', color: 'text.secondary' }} />
                    <Typography color="text.secondary">{trip.rides.length}</Typography>
                  </Stack>
                  <TripOverrideCitiesField trip={trip} />
                </Box>
              )}
              <Box component="dl" direction="row" sx={{
                ...style.dataList,
                ...(isGhostTrip(trip) && { gridTemplateColumns: { xs: '1fr', md: '260px 1fr' } })
              }}>
                {isGhostTrip(trip) ? (
                    <dt>
                      <Typography color="text.secondary">Return Pickup in {trip.originCity.cityName} </Typography>
                    </dt>
                ) : (
                  <dt>
                    <Typography color="text.secondary">Departure</Typography>
                  </dt>
                )}
                <dd>
                  {trip && (
                    <>
                      <Box display="flex" alignItems="center" gap="8px">
                        <Typography>
                          {f.date.toTimezoneLocaleString(
                            displayTrip.departureTime,
                            trip.originCity.timeZone,
                            f.date.DateFormats.TIME_SIMPLE_WITH_TIMEZONE,
                          )}
                        </Typography>
                        {canAdjustTripTime && (
                          <Button
                            size="small"
                            onClick={() => {
                              setAdjustTimeOpen(true);
                              // The daily-window trip prop can be minutes to
                              // hours stale (a scheduler nudges undriven
                              // trips' departureTime every ~15 min, and once
                              // it's pushed outside the loaded window,
                              // TRIP_UPDATED silently stops applying). Refetch
                              // so the dialog's preview is computed against
                              // the server's current departureTime instead.
                              fetchFullTripData();
                            }}
                          >
                            Edit
                          </Button>
                        )}
                      </Box>
                      {dispatchDelayMinutes > 0 && (
                        <Typography color="error.main" sx={{ fontStyle: 'italic' }}>
                          {` Delayed ${dispatchDelayMinutes} minutes finding a driver
                        (was ${f.date.toTimezoneLocaleString(
                          dispatchDelayBaseline,
                          trip.originCity.timeZone,
                          f.date.DateFormats.TIME_SIMPLE_WITH_TIMEZONE,
                        )})`}
                        </Typography>
                      )}
                      {opsAdjustmentBaseline && (
                        <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {` Adjusted by ops (was ${f.date.toTimezoneLocaleString(
                            opsAdjustmentBaseline,
                            trip.originCity.timeZone,
                            f.date.DateFormats.TIME_SIMPLE_WITH_TIMEZONE,
                          )})`}
                        </Typography>
                      )}
                    </>
                  )}
                </dd>
                {trip && isGhostTrip(trip) && (
                  <>
                    <dt>
                      <Typography color="text.secondary">Ghost Leg Departing from {trip.destinationCity.cityName} </Typography>
                    </dt>
                    <dd>
                      <Typography>
                        {trip.ghostLegDepartureTime
                          ? f.date.toTimezoneLocaleString(
                              trip.ghostLegDepartureTime,
                              trip.originCity.timeZone,
                              f.date.DateFormats.TIME_SIMPLE_WITH_TIMEZONE,
                            )
                          : 'Not set yet'}
                      </Typography>
                    </dd>
                  </>
                )}
                <dt>
                  <Typography color="text.secondary">Driver</Typography>
                </dt>
                <dd>{trip && <TripDriverField trip={trip} outboundTrip={outboundTrip} excludedRiderIds={excludedRiderIds} />}</dd>
                <dt>
                  <Box display="flex" alignItems="center" gap="4px">
                    <PhoneIcon sx={{ fontSize: '16px', color: 'text.secondary' }} />
                    <Typography color="text.secondary">Phone</Typography>
                  </Box>
                </dt>
                <dd>{trip && trip.driver ? f.phone.toInternational(trip.driver.phoneNumber) : ''}</dd>
                <dt>
                  <Typography color="text.secondary">Vehicle</Typography>
                </dt>
                {vehicle && (
                  <dd>
                    <Typography>
                      {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.licensePlate}
                    </Typography>
                  </dd>
                )}
                {trip && trip.fleetVehicle && (
                  <>
                    <dt>
                      <Typography color="text.secondary">Fleet Vehicle</Typography>
                    </dt>
                    <dd>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Chip
                          label="Fleet"
                          size="small"
                          sx={(theme) => ({
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(96, 165, 250, 0.18)' : '#DBEAFE',
                            color: theme.palette.mode === 'dark' ? '#93C5FD' : '#1E40AF',
                            fontWeight: 600,
                          })}
                        />
                        <Typography>
                          {trip.fleetVehicle.displayName}
                          {(trip.fleetVehicle.year || trip.fleetVehicle.make || trip.fleetVehicle.model) && ' — '}
                          {[trip.fleetVehicle.year, trip.fleetVehicle.make, trip.fleetVehicle.model]
                            .filter(Boolean)
                            .join(' ')}
                        </Typography>
                      </Stack>
                    </dd>
                  </>
                )}
                {trip && trip.confirmationStatus && (
                  <>
                    <dt>
                      <Typography color="text.secondary">Confirmation</Typography>
                    </dt>
                    <dd>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {getConfirmationStatusChip(trip.confirmationStatus)}
                        {CONFIRMATION_STATUS_DESCRIPTIONS[trip.confirmationStatus] && (
                          <Tooltip
                            title={CONFIRMATION_STATUS_DESCRIPTIONS[trip.confirmationStatus]}
                            arrow
                          >
                            <Box component="span" sx={{ display: 'inline-flex' }}>
                              <InfoIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            </Box>
                          </Tooltip>
                        )}
                        {trip.confirmedAt && (
                          <Typography color="text.secondary" sx={{ fontSize: '14px' }}>
                            {moment(trip.confirmedAt).fromNow()}
                          </Typography>
                        )}
                      </Stack>
                    </dd>
                  </>
                )}
                <dt>
                  <Typography color="text.secondary">Payout</Typography>
                </dt>
                <dd>
                  {trip && <TripPayoutField trip={trip} />}
                  {trip && trip.driverPaymentStatus && getPaymentStatusChip(trip.driverPaymentStatus)}
                  {trip && trip.dispatchStatus === 'attention' && (
                    <>
                      <ErrorIcon color="error" sx={{ fontSize: 20, ml: '8px', mr: '4px' }} /> Needs Attention
                    </>
                  )}
                </dd>
                <dt>
                  <Typography color="text.secondary">Views</Typography>
                </dt>
                <dd>
                  {trip && (
                    <Typography>
                      {trip.viewCount} {f.string.noun('time', 'times', trip.viewCount)} by {trip.viewedByCount}{' '}
                      {f.string.noun('driver', 'drivers', trip.viewedByCount)}
                    </Typography>
                  )}
                </dd>
              </Box>
              <Divider />
              <Box>
                <Stack direction="row" spacing="6px" alignItems="center" mb="4px">
                  <Typography sx={{ fontSize: { xs: '17px', md: '20px' }, fontWeight: { xs: 600, md: 400 } }}>
                    Riders
                  </Typography>
                  {exclusionMatchError && (
                    <Tooltip
                      title="Couldn't load the Rider-Driver Exclusions list — an excluded pairing here may not be flagged."
                      arrow
                    >
                      <span style={{ display: 'inline-flex' }}>
                        <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                      </span>
                    </Tooltip>
                  )}
                </Stack>
                {trip && (
                  <RidersList
                    rides={trip.rides}
                    trip={trip}
                    fullTripData={fullTripData}
                    onRideLocationUpdated={fetchFullTripData}
                    excludedRiderIds={excludedRiderIds}
                  />
                )}
              </Box>
              <Divider />
              {trip && trip.cancelledRides.length > 0 && (
                <>
                  <Box>
                    <Typography sx={{ fontSize: { xs: '17px', md: '20px' }, fontWeight: { xs: 600, md: 400 } }}>Cancelled riders</Typography>
                    {trip && <RidersList rides={trip.cancelledRides} trip={trip} fullTripData={fullTripData} />}
                  </Box>
                  <Divider />
                </>
              )}
              <Box>
                <Typography sx={{ fontSize: { xs: '17px', md: '20px' }, fontWeight: { xs: 600, md: 400 }, marginBottom: '20px' }}>Activity</Typography>
                {trip && <TripActivity trip={trip} locationData={locationData} fullTripData={fullTripData} />}
              </Box>
              <Divider />
              <Box>{trip && <TripEventLog trip={trip} />}</Box>
              <Divider />
              {returnTrip && getPrevNextTripComponent(returnTrip)}
              {outboundTrip && getPrevNextTripComponent(outboundTrip)}
              {!fullScreen && <Divider />}
            </Stack>
          </Grid>
          {fullScreen && (
            <Grid item xs="auto" sx={{ display: { xs: 'none', md: 'block' } }}>
              <Divider orientation="vertical" />
            </Grid>
          )}
          <Grid item xs={12} md={fullScreen ? 'auto' : 12} sx={{ minWidth: 0 }}>
            <Box>{trip && <TripItinerary trip={trip} />}</Box>
            <Box>
              <Typography sx={{ fontSize: { xs: '17px', md: '20px' }, fontWeight: { xs: 600, md: 400 }, marginBottom: '12px' }}>Map</Typography>
              <ErrorBoundary label="TripMap" message="The map couldn't be loaded.">
                {trip && <TripMap trip={trip} />}
              </ErrorBoundary>
            </Box>
            <Box sx={{ marginTop: '80px' }}>
              <Typography sx={{ fontSize: { xs: '17px', md: '20px' }, fontWeight: { xs: 600, md: 400 }, marginBottom: '12px' }}>Driver Location History</Typography>
              <ErrorBoundary label="TripLocationHistory" message="The location history map couldn't be loaded.">
                {trip && <TripLocationHistory trip={trip} locationData={locationData} fullTripData={fullTripData} />}
              </ErrorBoundary>
            </Box>
            <Box sx={{ marginTop: '30px', marginBottom: '24px' }}>
              <Typography sx={{ fontSize: { xs: '17px', md: '20px' }, fontWeight: { xs: 600, md: 400 }, marginBottom: '12px' }}>Driver location → First pickup</Typography>
              <ErrorBoundary label="DriverLocation" message="The driver location map couldn't be loaded.">
                {trip && <DriverLocation trip={trip} />}
              </ErrorBoundary>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      {trip && canAdjustTripTime && (
        <AdjustTripTimeDialog
          open={adjustTimeOpen}
          onClose={() => setAdjustTimeOpen(false)}
          trip={displayTrip}
          onSuccess={() => {
            // Refetch the full trip so departure time, dispatch-delay/ops
            // labels, and the ride list all reflect the change immediately
            // (getDailyTrips would unmount this dialog mid-flight via its
            // data: [] reset, so it's deliberately not used here). The ETA
            // cache is invalidated server-side by the adjustment, so it
            // needs its own refresh too.
            fetchFullTripData();
            getTripETA(trip.id)(dispatch);
          }}
        />
      )}
    </Dialog>
  );
};

export default TripDetailsDialog;
